import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Loader, ErrorFallback, TextInput, InputSwitch } from 'smart-react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faLockOpen } from '@fortawesome/free-solid-svg-icons';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import {
  buildNotification,
  GenerateNotification,
  isValidate,
  isFromValid,
  DescriptionTextArea,
} from 'smart-react';
import {
  createGlobalConfiguration,
  updateGlobalConfiguration,
  getEncryptDecryptData,
} from '../Services/GlobalConfigurationService';
import { isAdminRole } from '../../../../Utils/Auth/ValidatePermissions';
import { base64ToObject } from '../../../../Utils/Common/commonUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import { Form, Field } from '@progress/kendo-react-form';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { Error } from '@progress/kendo-react-labels';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../Core/Context/AuthContext';
import NoPermission from '../../../Core/NoPermission/NoPermission';
import { isEncryptedValue } from '../Utils/Utils';
import { getGlobalConfiguration } from '../Services/GlobalConfigurationService';
import { SplitButton, SplitButtonItem } from '@progress/kendo-react-buttons';
import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
/**
 * Add New Global Configuration main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Global Configuration component.
 */
const AddGlobalConfiguration = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get tenantID using Auth Context
  const { state } = useLocation();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [isTextArea, setIsTextArea] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isEncrypt, setIsEncrypt] = React.useState(true);
  const [formData, setFormData] = React.useState({
    ConfigId: '',
    Value: '',
    Desc: '',
    Type: '',
    SubType: '',
    Source: '',
    Active: true,
  });
  const [noRecord, setNoRecord] = React.useState(false);
  const [tempState, setTempState] = React.useState(state);
  const [validateForm, setValidateForm] = React.useState(false);
  const requiredFields = ['ConfigId', 'Value'];
  const [editorField, setEditorField] = React.useState({
    name: 'Desc',
    value: formData?.Desc,
    label: 'Description',
  });

  /**
   * Get Configuration Detail from API for edit Configuration
   * @param {*} stateData
   */
  const fetchConfigurationData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getGlobalConfiguration({
        data: refObj,
      });
      const testData = response?.Payload;
      if (testData) {
        setTempState({ ...stateData, dataSet: testData, edit: true });
        setFormData(testData);
        setIsEncrypt(!isEncryptedValue(testData?.Value));
        setNoRecord(false);
        setDetailsDataModified(true);
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState || (refId && refId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchConfigurationData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setDetailsDataModified(true);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * submit the Global Configuration data to api
   * @param {boolean} isExit
   * @returns {void}
   */
  const setEncryptDecryptData = async () => {
    if (!formData?.Value) {
      return;
    }
    let data = {
      Value: formData?.Value,
    };
    setLoading(true);
    const response = await getEncryptDecryptData({
      data: data,
      isEncrypt,
    });
    if (response?.Payload) {
      setFormData((prevState) => ({
        ...prevState,
        Value: response?.Payload,
      }));
      setIsEncrypt(!isEncrypt);
    }
    setLoading(false);
  };

  /**
   * submit the Global Configuration data to api
   * @param {boolean} isExit
   * @returns {void}
   */
  const onSave = async ({ isExit = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData = {};
    const data = {
      ...formData,
    };
    let response;
    let method;

    if (tempState?.edit) {
      method = updateGlobalConfiguration;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createGlobalConfiguration;
      notificationMetaData = CREATE_MESSAGE;
      setFormData({
        ConfigId: '',
        Value: '',
        Desc: '',
        Type: '',
        SubType: '',
        Source: '',
        Active: true,
      });
    }

    try {
      setValidateForm(false);
      response = await method({
        data,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        if (isExit) {
          navigate('/admin/global-configurations');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to Global Configurations listing screen
   */
  const onExit = () => {
    navigate('/admin/global-configurations');
  };

  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className='container mx-auto'>
        {!isAdminRole(user) && <NoPermission />}
        {isAdminRole(user) && (
          <React.Suspense fallback={<Loader />}>
            {loading && <Loader />}
            {noRecord ? (
              <NoRecordFound
                recordNotFoundText={'No Data found'}
                onExit={onExit}
              />
            ) : (
              <>
                <div className={`${isTextArea ? '' : 'k-d-none'}`}>
                  <DescriptionTextArea
                    onChange={handleInputChange}
                    label={'Description'}
                    renderTypes={!detailsDataModified ? 'ReadOnly' : ''}
                    validateForm={validateForm}
                    formData={formData}
                    setFormData={setFormData}
                    detailsDataModified={detailsDataModified}
                    isTextArea={isTextArea}
                    setIsTextArea={setIsTextArea}
                    editorField={editorField}
                    setEditorField={setEditorField}
                    row={2}
                  />
                </div>
                <div
                  className={`add-rf-profile-group main-page-section add-page-section ${!isTextArea ? '' : 'k-d-none'}`}
                >
                  <Card className='tophead'>
                    <CardHeader>
                      <div className='action-buttons-container'>
                        <Link
                          className='action-icon-wrapper !k-d-block'
                          to={`/admin/global-configurations`}
                        >
                          <FontAwesomeIcon
                            className='k-mr-2 k-pt-3'
                            icon={faArrowLeft}
                          />
                        </Link>
                        <div>
                          <p className='display-text k-mb-0'>
                            Config Id: {formData?.ConfigId}
                          </p>
                        </div>
                      </div>
                      {formData?.Desc && (
                        <div className='desciption-label'>
                          <span className='k-d-block k-mb-2'>
                            {formData?.Desc}
                          </span>
                        </div>
                      )}
                    </CardHeader>
                  </Card>
                  <Card className='k-mt-3 detail-card'>
                    <CardHeader>
                      <h6 className='card-title-secondary'>Details</h6>
                    </CardHeader>
                    <CardBody className='detail-card-body'>
                      <div>
                        <Form
                          onSubmit={onSave}
                          render={(formRenderProps) => (
                            <div className='card-content'>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper ${isValidate(validateForm, formData?.ConfigId) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'ConfigId'}
                                    id={'ConfigId'}
                                    name={'ConfigId'}
                                    data={formData?.ConfigId}
                                    onChange={handleInputChange}
                                    label={'Config Id'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      tempState?.edit ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  {isValidate(
                                    validateForm,
                                    formData?.ConfigId,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                                <div
                                  className={`field-wrapper data-field-wrapper data-icon-input ${isValidate(validateForm, formData?.Value) ? 'field-invalid' : 'field-valid'}`}
                                >
                                  <Field
                                    key={'Value'}
                                    id={'Value'}
                                    name={'Value'}
                                    data={formData?.Value}
                                    onChange={handleInputChange}
                                    label={'Value'}
                                    type={'text'}
                                    component={TextInput}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    optional={false}
                                    requiredField={true}
                                  />
                                  <span
                                    onClick={() => {
                                      setEncryptDecryptData();
                                    }}
                                    className='icon-wrapper'
                                  >
                                    {isEncrypt ? (
                                      <FontAwesomeIcon
                                        className='field-icon k-mr-1'
                                        icon={faLock}
                                      />
                                    ) : (
                                      <FontAwesomeIcon
                                        className='field-icon k-mr-1'
                                        icon={faLockOpen}
                                      />
                                    )}
                                  </span>
                                  {isValidate(
                                    validateForm,
                                    formData?.Value,
                                  ) && (
                                    <Error>This field cannot be empty</Error>
                                  )}
                                </div>
                              </div>
                              <div className='k-d-flex single-field-row'>
                                <div
                                  className={`field-wrapper field-wrapper-description field-wrapper-100`}
                                >
                                  <DescriptionTextArea
                                    onChange={handleInputChange}
                                    label={'Description'}
                                    renderTypes={
                                      !detailsDataModified ? 'ReadOnly' : ''
                                    }
                                    validateForm={validateForm}
                                    formData={formData}
                                    setFormData={setFormData}
                                    detailsDataModified={detailsDataModified}
                                    isTextArea={isTextArea}
                                    setIsTextArea={setIsTextArea}
                                    editorField={{
                                      name: 'Desc',
                                      value: formData?.Desc,
                                      label: 'Description',
                                    }}
                                    setEditorField={setEditorField}
                                    row={2}
                                  />
                                </div>
                              </div>
                              <div className='single-field-row k-mt-4 single-row-60'>
                                <div className={`field-wrapper`}>
                                  <Field
                                    name='Active'
                                    component={InputSwitch}
                                    label='Active'
                                    onLabel='Yes'
                                    offLabel='No'
                                    id='Active'
                                    checked={formData?.Active}
                                    onChange={handleInputChange}
                                    disabled={!detailsDataModified}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>
                  {detailsDataModified && (
                    <Card className='k-mt-3'>
                      <CardHeader>
                        <div className='k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0'>
                          <>
                            <Button
                              icon={'cancel'}
                              onClick={() => onExit()}
                              type='button'
                            >
                              Cancel
                            </Button>
                            <SplitButton
                              text='save'
                              themeColor={'primary'}
                              onButtonClick={() => onSave({ isExit: false })}
                              onItemClick={handleSaveSplit}
                            >
                              <SplitButtonItem text='Save & Close' />
                            </SplitButton>
                          </>
                        </div>
                      </CardHeader>
                    </Card>
                  )}
                </div>
              </>
            )}
          </React.Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
};
export default AddGlobalConfiguration;
